import { LoaderFullPage } from '@cart-ui/LoaderFullPage/LoaderFullPage'
import { datadogRum } from '@datadog/browser-rum'
import { useFastpassMerchantGoogleAnalyticEvent } from '@dni-analysis'
import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { useApiECommerceUpdateUserPaymentToken } from '@hooks-api/eCommerceV2/use-api-e-commerce-update-user-payment-token'
import { type CheckoutOrder } from '@hooks-api/types/dataModel/orders'
import { PaymentMethodType, PaymentServiceType } from '@hooks-api/types/dataModel/payment'
import { CheckoutPayApplePayModule, TappayApplePayModule } from '@payments'
import { Button } from '@pets-ui/Button/Button'
import { HStack } from '@pets-ui/HStack/HStack'
import { CartFilled } from '@pets-ui/icons'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useContextPayment } from '../../../fp-b2b-shared/components/PaymentProvider/PaymentProvider'
import { useCheckout } from '../../../fp-b2b-shared/hooks/use-checkout'
import { useHandleValidateCreditCard } from '../../../fp-b2b-shared/hooks/use-handle-validate-credit-card'

interface RetryPaymentFooterActionProps {
  isLoading?: boolean
  onSubmit?: (data: CheckoutOrder) => void
}

export function RetryPaymentFooterAction({ isLoading, onSubmit }: RetryPaymentFooterActionProps) {
  const { projectCode, countryCode, campaignId } = useCheckout()
  const { configuredPaymentModule, selectedPayment, isLoadingPayment, creditCardholderInfo, isSdkInitialized } = useContextPayment()
  const { sendRepayOrderEvent } = useFastpassMerchantGoogleAnalyticEvent({ projectCode, countryCode, campaignId })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const {
    handleSubmit,
    setError,
    getValues,
  } = useFormContext<CheckoutOrder>()

  const { handleValidateCreditCard } = useHandleValidateCreditCard(configuredPaymentModule, selectedPayment?.id ?? null, isSdkInitialized)
  /** Update cardholder info */
  const { trigger: updateUserPaymentToken } = useApiECommerceUpdateUserPaymentToken()
  const handleUpdateCreditCardholderInfo = useCallbackEvent(async () => {
    if (!creditCardholderInfo) return
    const cardholder = {
      name_en: creditCardholderInfo.name_en,
      email: creditCardholderInfo.email,
      phone_number: creditCardholderInfo.phone_number,
      phone_number_country_code: creditCardholderInfo.phone_number_country_code,
    }
    try {
      await updateUserPaymentToken({
        country_code: countryCode,
        token_id: Number(creditCardholderInfo?.token),
        cardholder,
      })
    }
    catch (error) {
      /** 更新持卡人資訊是真正結帳(create order + payment execution)前的動作，考量到cardholder info未必導致刷卡失敗，因此即使update card info 失敗，也不阻擋結帳流程 */
      console.error(`Update cardholder info error: ${error}`)
    }
  })

  const handleValidSubmit = async (data: CheckoutOrder) => {
    setIsSubmitting(true)

    const formValues = getValues()
    /** 電話號碼格式: 純數字 或 數字#分機 */
    if (!/^\d+(#\d+)?$/.test(formValues.billing_address_phone)) {
      setError('merchantInfoValidation', { message: '請輸入正確的電話號碼' })
      setIsSubmitting(false)
      return
    }
    /**
     * Auto RHF validation include PaymentCreditCard, PaymentEcpayAtm, ShippingMethod, MerchantInfo
     */
    try {
      const isTappayApplePay
        = selectedPayment?.service_type === PaymentServiceType.TAPPAY
          && selectedPayment.type === PaymentMethodType.APPLE_PAY
          && configuredPaymentModule instanceof TappayApplePayModule

      const isCheckoutPayApplePay
        = selectedPayment?.service_type === PaymentServiceType.CHECKOUTPAY
          && selectedPayment.type === PaymentMethodType.APPLE_PAY
          && configuredPaymentModule instanceof CheckoutPayApplePayModule

      if (isTappayApplePay || isCheckoutPayApplePay) {
        await configuredPaymentModule.createApplePayToken()
      }

      onSubmit?.(data)
    }
    catch (error) {
      console.error(`[STM Checkout] ${error}`)
    }
    finally {
      setIsSubmitting(false)
    }
  }

  const handlePurchaseSubmit = useCallbackEvent(() => {
    try {
      handleValidateCreditCard()
      sendRepayOrderEvent()
      handleUpdateCreditCardholderInfo()
      handleSubmit(handleValidSubmit)()
    }
    catch (error) {
      datadogRum.addError(new Error(`[STM Checkout] handlePurchaseSubmit
        error: ${error}`), {
        function: 'handlePurchaseSubmit',
        error,
      })
      console.error(`[STM Checkout] ${error}`)
    }
  })

  return (
    <>
      {isSubmitting && <LoaderFullPage />}
      <Button
        onClick={handlePurchaseSubmit}
        disabled={isLoading || isSubmitting || isLoadingPayment}
        loading={isLoading}
      >
        <HStack gap={1} alignItems="center" justifyContent="center">
          <CartFilled color={colors.white} fontSize={20} />
          <Text variant="subtitle" color={colors.white}>
            重新付款
          </Text>
        </HStack>
      </Button>
    </>
  )
}
